/**
 * HelpSupportTab - Help & Support settings tab
 * Extracted from SettingsPage to reduce file size
 */
import { Button } from "@/components/ui/button";
import {
  HelpCircle,
  Mail,
  Clock,
  Star,
  FileText,
  Check,
  ExternalLink,
} from "lucide-react";

export function HelpSupportTab() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-primary" />
        Help & Support
      </h2>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Contact Support */}
        <div className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">Contact Support</h3>
              <p className="text-slate-600 text-sm mt-1 mb-4">
                Have a question or need help? Our support team is ready to assist you.
              </p>
              <Button
                onClick={() => window.location.href = "mailto:support@carepilotusa.com?subject=CarePilot Support Request"}
                className="gap-2"
                data-testid="contact-support-btn"
              >
                <Mail className="w-4 h-4" />
                Email Support
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                support@carepilotusa.com
              </p>
            </div>
          </div>
        </div>

        {/* Schedule Demo/Training */}
        <div className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-200 rounded-lg">
              <Clock className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">Schedule Training</h3>
              <p className="text-slate-600 text-sm mt-1 mb-4">
                Need a walkthrough or training session? Book time with our team.
              </p>
              <Button
                variant="outline"
                className="gap-2 border-emerald-300 hover:bg-emerald-50"
                onClick={() => window.open("https://cal.com/team/carepilot-usa/", "_blank")}
                data-testid="schedule-training-btn"
              >
                <Clock className="w-4 h-4" />
                Schedule Session
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Sales Inquiries */}
        <div className="p-6 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl border border-amber-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-200 rounded-lg">
              <Star className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">Sales & Upgrades</h3>
              <p className="text-slate-600 text-sm mt-1 mb-4">
                Interested in upgrading your plan or have billing questions?
              </p>
              <Button
                variant="outline"
                className="gap-2 border-amber-300 hover:bg-amber-50"
                onClick={() => window.location.href = "mailto:info@carepilotusa.com?subject=CarePilot Sales Inquiry"}
                data-testid="contact-sales-btn"
              >
                <Mail className="w-4 h-4" />
                Contact Sales
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                info@carepilotusa.com
              </p>
            </div>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-slate-200 rounded-lg">
              <FileText className="w-6 h-6 text-slate-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">Quick Tips</h3>
              <ul className="text-slate-600 text-sm mt-2 space-y-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Use the sidebar to navigate between modules</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Set up notifications in Settings → Notifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Configure staff permissions in Settings → Staff Permissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Export data from EVV, Payroll, or QuickBooks sections</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Support Hours */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-sm text-slate-600 text-center">
          <span className="font-medium">Support Hours:</span> Monday - Friday, 9:00 AM - 6:00 PM EST
        </p>
      </div>
    </div>
  );
}
